import { supabase } from './supabase';

/**
 * Single standardized entry point for notifying a host about an admin
 * decision (event approved/rejected, edit approved/rejected, deletion
 * approved/denied, review-removal approved/denied, etc).
 *
 * Every admin moderation action in this app should go through this instead
 * of hand-rolling its own `notifications` insert, so in-app + email stay
 * consistent and neither gets forgotten when a new action is added.
 *
 * Channels:
 * - In-app: always sent — a direct insert into `notifications`. Admins are
 *   allowed to insert notifications for other users via the
 *   `admins_can_insert_notifications` RLS policy (is_admin_user()).
 * - Email: sent only when `emailTemplate` is provided, via the
 *   `send-notification-email` edge function (Resend). That function no-ops
 *   gracefully if RESEND_API_KEY isn't configured server-side.
 * - Push: not implemented — there is no Firebase project / FCM sending
 *   function yet (see the Flutter app's NotificationDispatchService for the
 *   matching placeholder and rationale). Nothing to call from here either.
 *
 * @param {object} params
 * @param {string} params.userId - the host's user id (auth.users id)
 * @param {string} params.title - in-app notification title
 * @param {string} params.body - in-app notification body
 * @param {string} params.notificationType - notification_type column value
 * @param {string} [params.eventId]
 * @param {string} [params.emailTemplate] - one of send-notification-email's templates
 * @param {string} [params.emailTo]
 * @param {string} [params.emailSubject]
 * @param {Record<string,string>} [params.emailData]
 */
export async function notifyHost({
  userId,
  title,
  body,
  notificationType,
  eventId = null,
  emailTemplate = null,
  emailTo = null,
  emailSubject = null,
  emailData = {},
}) {
  if (!userId) return;

  try {
    await supabase?.from('notifications')?.insert({
      user_id: userId,
      event_id: eventId,
      title,
      body,
      notification_type: notificationType,
      is_read: false,
    });
  } catch (err) {
    console.error('notifyHost: in-app insert failed', err);
  }

  if (emailTemplate && emailTo) {
    try {
      await supabase?.functions?.invoke('send-notification-email', {
        body: {
          to: emailTo,
          subject: emailSubject || title,
          template: emailTemplate,
          data: emailData,
        },
      });
    } catch (err) {
      console.error('notifyHost: email send failed', err);
    }
  }
}
